import { FormArray, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';

import { InputType, Question as BaseQuestion, QuestionType } from '@empathy/survey/base';

import { IAIPromptForm } from './ai-prompt-form.interface';
import * as CustomValidators from './custom-validators';
import { IMediaForm } from './media-form.interface';

export class Question extends BaseQuestion {
  getFormControl(): FormControl | FormArray | FormGroup {
    return this.mapFormControl();
  }

  private mapFormControl(): FormControl | FormArray | FormGroup {
    switch (this.type) {
      case QuestionType.InfoPage: {
        return new FormControl('', { nonNullable: true });
      }
      case QuestionType.CarouselMultiChoice:
      case QuestionType.Carousel:
      case QuestionType.CarouselRating: {
        const carouselGroup = new FormGroup({}, this.requiredInd ? Validators.required : null);

        this.cards.forEach((card, index) => {
          const cardId = carouselGroup.get(card.id) ? `${card.id}_${index}` : card.id;
          card.id = cardId;
          const cardGroup = new FormGroup(
            {
              value: new FormControl('', { nonNullable: true }),
              otherText: new FormControl('', { nonNullable: true })
            },
            CustomValidators.ChoiceValidator(this.requiredInd, this.useOtherInd)
          );

          if (card.visible === false) {
            cardGroup.disable();
          }

          carouselGroup.addControl(card.id, cardGroup);
        });

        return carouselGroup;
      }
      case QuestionType.Dropdown:
      case QuestionType.RankSort:
      case QuestionType.Rating:
      case QuestionType.SingleChoice: {
        const choicesGroup = new FormGroup(
          {
            value: new FormControl('', { nonNullable: true }),
            otherText: new FormControl('', { nonNullable: true })
          },
          CustomValidators.ChoiceValidator(this.requiredInd, this.useOtherInd)
        );
        return choicesGroup;
      }
      case QuestionType.MultiChoice: {
        const multiChoicesGroup = new FormGroup(
          {
            value: new FormControl('', { nonNullable: true }),
            otherText: new FormControl('', { nonNullable: true })
          },
          [CustomValidators.ChoiceValidator(this.requiredInd, this.useOtherInd)]
        );

        return multiChoicesGroup;
      }
      case QuestionType.Input:
      case QuestionType.Essay: {
        return new FormControl('', { nonNullable: true, validators: this.mapInputValidators(), updateOn: 'change' });
      }
      case QuestionType.Heatmap: {
        return new FormControl('', { nonNullable: true, validators: this.mapHeatmapValidators() });
      }
      case QuestionType.FileUpload: {
        return new FormArray<FormGroup<IMediaForm>>([], CustomValidators.MinMaxFiles(this.minLength, this.maxLength));
      }
      case QuestionType.AIChat: {
        return new FormArray<FormGroup<IAIPromptForm>>([], CustomValidators.AiChatAnswered(this.requiredInd));
      }
      case QuestionType.Ranking: {
        return new FormControl('', { nonNullable: true, validators: this.mapRequiredValidators() });
      }
      default:
        return new FormControl('', { nonNullable: true, validators: this.mapRequiredValidators() });
    }
  }

  private mapRequiredValidators(): ValidatorFn[] {
    const validators: ValidatorFn[] = [];
    if (this.requiredInd) {
      validators.push(Validators.required);
    }

    return validators;
  }

  private mapInputValidators(): ValidatorFn[] {
    const validators: ValidatorFn[] = [];

    if (this.requiredInd) {
      validators.push(Validators.required);
    }

    if (this.inputType === InputType.Email) {
      validators.push(Validators.email);
    }

    if (this.inputType === InputType.Text) {
      if (this.minTextLength > 0) {
        validators.push(Validators.minLength(this.minTextLength));
      }

      if (this.maxTextLength > 0) {
        validators.push(Validators.maxLength(this.maxTextLength));
      }
    }

    if (this.inputType === InputType.Numbers) {
      if (this.minLength > 0) {
        validators.push(Validators.min(this.minLength));
      }

      if (this.maxLength > 0) {
        validators.push(Validators.max(this.maxLength));
      }
    }

    validators.push();

    return validators;
  }

  private mapHeatmapValidators(): ValidatorFn[] {
    const validators: ValidatorFn[] = [];

    if (this.requiredInd) {
      validators.push(Validators.required);
    }

    validators.push(CustomValidators.MinMaxAreas(this.minLength, this.maxLength));

    return validators;
  }
}
